import React from "react";
import Image from "next/image";
import { Box, Text, Flex, AspectRatio, type SpaceProps, type FlexProps } from "@chakra-ui/react";
import { Button } from "ui/button";
import { type QuoteProps } from "./types";

export type CenteredQuoteProps = {
  /**
   * Max width of the quote section.
   * @default "5xl"
   */
  maxW?: FlexProps["maxW"];
} & QuoteProps &
  SpaceProps;

export function CenteredQuote({
  theme = "light",
  quoteText,
  author,
  company,
  cta,
  maxW = "5xl",
  ...props
}: CenteredQuoteProps) {
  const logoFilter = theme === "dark" ? "brightness(0) invert(1)" : undefined;

  return (
    <Flex
      align={{ base: "start", md: "center" }}
      color={`brand.text.${theme}`}
      direction="column"
      gap={{ base: "big", md: "bigger" }}
      justify={{ base: "start", md: "center" }}
      w="100%"
      maxW={maxW}
      mx="auto"
      {...props}
    >
      {company?.logo && (
        <>
          <AspectRatio
            display={{ base: "block", md: "none" }}
            ratio={company.logoRatio ?? 4 / 1}
            width={{ base: "100px", sm: "130px" }}
            filter={logoFilter}
            pos="relative"
          >
            <Image
              fill
              sizes="100%"
              alt={`${company.name} logo.`}
              src={`${company.logo}?w=320`}
              style={{ objectFit: "contain", objectPosition: "left" }}
            />
          </AspectRatio>

          <AspectRatio
            display={{ base: "none", md: "block" }}
            ratio={company.logoRatio ?? 4 / 1}
            width={{ base: "160px", lg: "180px" }}
            filter={logoFilter}
            pos="relative"
          >
            <Image
              fill
              sizes="100%"
              alt={`${company.name} logo.`}
              src={`${company.logo}?w=320`}
              style={{ objectFit: "contain", objectPosition: "center" }}
            />
          </AspectRatio>
        </>
      )}

      <Text
        textStyle={{ base: "headline3", sm: "headline2", md: "headline1" }}
        textAlign={{ base: "left", md: "center" }}
      >
        “{quoteText}”
      </Text>

      <Flex align={{ base: "start", md: "center" }} gap="big" flexDir={{ base: "column", md: "row" }}>
        <Box textStyle={{ base: "caption2", sm: "caption1" }} textAlign={cta ? "left" : { base: "left", md: "center" }}>
          <Text fontWeight="bold">{author.name}</Text>
          <Text mt="tinier" maxW={{ base: "100%", md: "300px" }} whiteSpace="pre-wrap">
            {author.job} {company && `at ${company.name}`}
          </Text>
        </Box>

        {cta && (
          <Button hasRightChevron href={cta.href} variant="primary" flexShrink={0}>
            {cta.text}
          </Button>
        )}
      </Flex>
    </Flex>
  );
}
